import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import JobCard from "../components/JobCard";
import VerifiedBadge from "../components/VerifiedBadge";
import StarRating from "../components/StarRating";
import { MapPin, Calendar, Users, Briefcase } from "lucide-react";

export default function EventDetail() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);

  useEffect(() => {
    api.get(`/events/${id}`).then(r => setEvent(r.data)).catch(() => {});
  }, [id]);

  if (!event) return <div className="p-12 text-center text-muted-foreground">Loading…</div>;

  const totalSlots = event.jobs?.reduce((s, j) => s + (j.total_slots || 0), 0) || 0;
  const confirmedSlots = event.jobs?.reduce((s, j) => s + (j.confirmed_count || 0), 0) || 0;

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10">
      {/* Event header */}
      <div className="card-flat mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <span className="label-eyebrow text-muted-foreground">Event</span>
            <h1 className="mt-1 font-display text-3xl sm:text-4xl tracking-tighter text-foreground">{event.name}</h1>
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-muted-foreground">
              {event.date && (
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-brand" />
                  {new Date(event.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                </span>
              )}
              {event.venue && (
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-brand" />
                  {event.venue}{event.location ? `, ${event.location}` : ""}
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <Users className="w-4 h-4 text-brand" />
                {confirmedSlots}/{totalSlots} staff confirmed
              </span>
            </div>
            {event.description && <p className="mt-3 text-sm text-muted-foreground">{event.description}</p>}
          </div>
        </div>

        {/* Poster info */}
        {event.poster_name && (
          <div className="mt-4 pt-4 border-t border-border flex items-center gap-3">
            {event.poster_avatar
              ? <img src={event.poster_avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
              : <div className="w-10 h-10 rounded-full bg-brand-tint grid place-items-center text-brand font-bold">{(event.poster_name)[0]}</div>}
            <div>
              <p className="text-sm font-medium text-foreground flex items-center gap-1">
                {event.poster_name}
                {event.poster_verified && <VerifiedBadge size={14} />}
              </p>
              <p className="text-xs text-muted-foreground">@{event.poster_username}</p>
              {event.poster_rating && <StarRating rating={event.poster_rating} count={event.poster_rating_count} size="sm" />}
            </div>
          </div>
        )}
      </div>

      {/* Job roles */}
      <h2 className="font-display text-2xl tracking-tight mb-4">
        Available roles <span className="text-muted-foreground text-lg">({event.jobs?.length || 0})</span>
      </h2>

      {!event.jobs?.length
        ? <div className="card-flat text-center text-muted-foreground py-10">No roles posted yet.</div>
        : <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {event.jobs.map(j => <JobCard key={j.id} job={j} />)}
          </div>
      }

      <div className="mt-6 text-center">
        <Link to="/" className="text-sm text-brand hover:underline">← Browse all jobs</Link>
      </div>
    </div>
  );
}
